import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Video } from "@/components/common/video";
import { Image } from "@/components/common/image";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import AgentVideoController from "./agent-video-controller";
import { TruncatedText } from "../common/truncated-text";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { AgentShareModal } from "../agent-share-modal";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { HashTagText } from "../common/hash-tag-text";
import { AccountCard } from "../common/account-card";
import { CopyClipboard } from "../common/copy-clipboard";

export interface AgentVideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  // 视频源与封面
  videoUrl: string;
  poster?: string;

  // 右侧信息
  avatarUrl: string;
  nickname: string;
  text: string;

  deleteLoading?: boolean;
  isAutoPush?: boolean;

  // 操作
  onCopyText?: (text: string) => void;
  onDownload?: (videoUrl: string) => void;
  onShare?: (videoUrl: string) => void;
  onReedit?: () => void;
  onDelete?: () => Promise<void>;
  onRegenerate?: () => void;
}

const CopySvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const ShareSvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49"></path>
  </svg>
);

export const AgentVideoModal: React.FC<AgentVideoModalProps> = ({
  open,
  onOpenChange,
  videoUrl,
  poster,
  avatarUrl,
  nickname,
  text,
  deleteLoading,
  isAutoPush,
  onCopyText,
  onDownload,
  onShare,
  onReedit,
  onDelete,
  onRegenerate,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { download } = useDownload();
  const { toast } = useToast();
  const handleCopy = async () => {
    try {
      if (onCopyText) {
        await Promise.resolve(onCopyText(text));
        toast.default({ description: t('agent_creation.agent_modal.copied') });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.default({ description: t('agent_creation.agent_modal.copied') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
    }
  };
  const handleDownload = async () => {
    try {
      if (onDownload) {
        await Promise.resolve(onDownload(videoUrl));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await download(videoUrl, "video");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] shadow-lg z-50 flex h-[90vh]"
        >
          {/* 左侧：视频区不设固定宽度，随视频自动撑开 */}
          <div className="relative group bg-black flex items-center justify-center flex-shrink-0 flex-grow-0 h-[90vh] aspect-[3/4]">,
            {/* 视频容器 */}
            <div className="relative w-full h-full flex items-center justify-center">
              <Video
                ref={videoRef}
                src={videoUrl}
                className="h-full w-auto"
                poster={poster}
                autoPlay
                preload="auto"
                controls={false}
              />
            </div>

            {/* 播放控制条 */}
            <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200">
              <AgentVideoController videoRef={videoRef} />
            </div>
          </div>

          <div className="p-6 w-[430px] h-full bg-white text-[rgba(56,65,74,1)] flex flex-col shrink-0 grow-0 gap-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <AccountCard
                  compact
                  className="size-10"
                  name={nickname}
                  avatar={avatarUrl}
                />
                <TruncatedText className="w-56 text-lg text-[rgba(16,24,40,1)] font-medium" text={nickname} />
              </div>
              <ModalCloseButton
                onClick={() => onOpenChange(false)}
              />
            </div>

            {/* 文案区 */}
            <div className="flex-1 overflow-y-auto text-sm text-[rgba(16,24,40,1)] font-normal">
              <HashTagText text={text} newLine />
            </div>

            <div className="flex w-full gap-3 justify-end items-center">
              <MoreActionPopover
                className="w-40"
                side="top"
                align="center"
                tiggerClassName="size-9 rounded-[8px]"
                tiggerIcon={
                  <IconButton
                    icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                  />
                }
              >
                {!isAutoPush && (
                  <>
                    <MoreActionItem
                      icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.reedit")}
                      onClick={() => {
                        onReedit?.();
                        onOpenChange(false);
                      }}
                    />
                    {/* <MoreActionItem
                      icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.regenerate_again")}
                      onClick={() => {
                        onRegenerate?.();
                        onOpenChange(false);
                      }}
                    /> */}
                  </>
                )}
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={async () => {
                    await onDelete?.();
                    onOpenChange(false);
                  }}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
              <CopyClipboard
                className="size-9 rounded-[8px]"
                iconClassName="w-5 h-5 text-[rgba(51,51,51,1)]"
                text={text}
                icon={<CopySvg className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
              />
              {/* <IconButton
                className="size-9 rounded-[8px]"
                icon={<CopySvg className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
                onClick={handleCopy}
              /> */}
              <IconButton
                className="size-9 rounded-[8px]"
                icon={<DownloadIcon className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
                onClick={handleDownload}
              />
              <AgentShareModal
                shouldFetchData={open}
                showDouyin
                showKuaishou
                douyinOptions={{
                  videoUrls: [videoUrl],
                }}
                kuaishouOptions={{
                  title: text,
                  coverUrl: poster,
                  videoUrl: videoUrl,
                }}
                showTiktok
                tiktokOptions={{
                  videoUrls: [videoUrl],
                }}
                trigger={
                  <TextButton
                    className="px-[22px] py-[10px] rounded-[8px] max-w-max text-sm"
                    icon={<PublishIcon className="w-5 h-5" />}
                    label={t('agent_creation.agent_modal.publish')}
                  />
                }
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentVideoModal;